import { createContext, useContext, useEffect, useState, useCallback, useRef, ReactNode } from "react";
import { Session, User } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";
import { ANGEL_BUSINESS_ID } from "@/constants";
import { resolveClientBusinessId } from "@/lib/resolveClientBusinessId";

const REVIEWBOOSTS_SERVICES_EMAIL = "reviewboosts.services@gmail.com";

interface AuthContextType {
  session: Session | null;
  user: User | null;
  profile: {
    subscription_status: string;
    subscription_valid_until: string | null;
    full_name: string | null;
    email: string | null;
    business_id: string | null;
    status: string;
    role?: "admin" | "employee" | "founder" | "super_admin" | null;
    is_vip?: boolean;
    subscription_plan?: string;
    is_founding_member?: boolean;
    locked_monthly_rate?: number | null;
    avatar_url?: string | null;
  } | null;
  role: "admin" | "employee" | null;
  loading: boolean;
  profileLoading: boolean;
  businessId: string | null;
  isFounder: boolean;
  signOut: () => Promise<void>;
  refreshProfile: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType>({
  session: null,
  user: null,
  profile: null,
  role: null,
  loading: true,
  profileLoading: true,
  businessId: null,
  isFounder: false,
  signOut: async () => {},
  refreshProfile: async () => {},
});

export const useAuth = () => useContext(AuthContext);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Session | null>(null);
  const [user, setUser] = useState<User | null>(null);
  const [profile, setProfile] = useState<AuthContextType["profile"]>(null);
  const [role, setRole] = useState<"admin" | "employee" | null>(null);
  const [loading, setLoading] = useState(true);
  const [profileLoading, setProfileLoading] = useState(true);
  const [businessId, setBusinessId] = useState<string | null>(null);
  const profileRetryDone = useRef(false);
  const isFounder = profile?.role === "founder";

  const fetchProfileAndRole = useCallback(async (userId: string, userEmail?: string | null) => {
    setProfileLoading(true);
    try {
      // Non-blocking: RPC failures (permissions, network) must not strand the UI on "Setting up your dashboard…"
      const { error: inviteRpcError } = await supabase.rpc("accept_pending_invite_for_current_user");
      if (inviteRpcError) {
        console.warn("[useAuth] accept_pending_invite_for_current_user:", inviteRpcError.message);
      }

      const [profileRes, roleRes, subRes, bizIdRes] = await Promise.all([
        supabase
          .from("profiles")
          .select("subscription_status, subscription_valid_until, full_name, email, business_id, status, role, is_vip, subscription_plan, is_founding_member, locked_monthly_rate, avatar_url")
          .eq("user_id", userId)
          .maybeSingle(),
        supabase.from("user_roles").select("role").eq("user_id", userId),
        supabase.rpc("has_active_subscription", { _user_id: userId }),
        supabase.rpc("get_user_business_id", { _user_id: userId }),
      ]);

      if (profileRes.error) {
        console.warn("[useAuth] profiles select:", profileRes.error.message);
      }
      if (roleRes.error) {
        console.warn("[useAuth] user_roles select:", roleRes.error.message);
      }
      if (subRes.error) {
        console.warn("[useAuth] has_active_subscription:", subRes.error.message);
      }
      if (bizIdRes.error) {
        console.warn("[useAuth] get_user_business_id:", bizIdRes.error.message);
      }

      const roles = (roleRes.data as { role?: string }[] | null) ?? [];
      const resolvedRole = roles.some((r) => r.role === "employee") ? "employee" : roles[0]?.role;
      const rpcBiz = bizIdRes.data as string | null | undefined;
      const isReviewboostsServices = !!userEmail && userEmail.toLowerCase().trim() === REVIEWBOOSTS_SERVICES_EMAIL;

      if (profileRes.data) {
        const bizActive = subRes.data === true;
        const row = profileRes.data as { subscription_status?: string; subscription_valid_until?: string | null; full_name?: string | null; email?: string | null; business_id?: string | null; status?: string; role?: "admin" | "employee" | "founder" | "super_admin" | null; is_vip?: boolean; subscription_plan?: string; is_founding_member?: boolean; locked_monthly_rate?: number | null; avatar_url?: string | null };
        const resolvedBusinessId = resolveClientBusinessId(rpcBiz, row.business_id, isReviewboostsServices);
        const rawStatus = row.subscription_status ?? "inactive";
        setProfile({
          ...row,
          business_id: resolvedBusinessId ?? row.business_id,
          subscription_status: rawStatus === "grace" ? "grace" : (bizActive ? "active" : rawStatus),
          subscription_valid_until: row.subscription_valid_until ?? null,
          status: row.status ?? "active",
          is_vip: row.is_vip ?? false,
        });
        setBusinessId(resolvedBusinessId);
      } else {
        // Profile row missing (race); still align tenant with DB so inventory RLS matches client filters
        const resolvedBusinessId = resolveClientBusinessId(rpcBiz, null, isReviewboostsServices);
        setProfile(null);
        setBusinessId(resolvedBusinessId);
      }
      setRole((resolvedRole === "admin" || resolvedRole === "employee" ? resolvedRole : null) as "admin" | "employee" | null);
    } catch (e) {
      console.error("[useAuth] fetchProfileAndRole failed:", e);
    } finally {
      setProfileLoading(false);
    }
  }, []);

  const refreshProfile = useCallback(async () => {
    if (user?.id) await fetchProfileAndRole(user.id, user.email ?? undefined);
  }, [user?.id, user?.email, fetchProfileAndRole]);

  // Self-heal: admin with no business (trigger race or legacy data) — call ensure_business then refresh
  useEffect(() => {
    if (profileLoading || !user?.id || isFounder) return;
    if (profile === null && !profileRetryDone.current) {
      profileRetryDone.current = true;
      const t = setTimeout(() => {
        fetchProfileAndRole(user.id, user.email ?? undefined);
      }, 1500);
      return () => clearTimeout(t);
    }
    const isReviewboostsServices = !!user?.email && user.email.toLowerCase().trim() === REVIEWBOOSTS_SERVICES_EMAIL;
    const needsEnsure = (profile && role === "admin" && !businessId) || (isReviewboostsServices && role === "admin" && (!businessId || businessId === ANGEL_BUSINESS_ID));
    if (needsEnsure) {
      supabase.rpc("ensure_business_for_admin").then(() => {
        fetchProfileAndRole(user!.id, user!.email ?? undefined);
      });
    }
  }, [profileLoading, user?.id, user?.email, profile, role, businessId, isFounder, fetchProfileAndRole]);

  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setSession(session);
      setUser(session?.user ?? null);
      if (session?.user) {
        fetchProfileAndRole(session.user.id, session.user.email ?? undefined);
      } else {
        profileRetryDone.current = false;
        setProfile(null);
        setRole(null);
        setBusinessId(null);
        setProfileLoading(false);
      }
      setLoading(false);
    });

    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);
      if (session?.user) {
        fetchProfileAndRole(session.user.id, session.user.email ?? undefined);
      } else {
        setProfileLoading(false);
      }
      setLoading(false);
    });

    return () => subscription.unsubscribe();
  }, [fetchProfileAndRole]);

  const signOut = async () => {
    await supabase.auth.signOut();
  };

  return (
    <AuthContext.Provider value={{ session, user, profile, role, loading, profileLoading, businessId, isFounder, signOut, refreshProfile }}>
      {children}
    </AuthContext.Provider>
  );
}
